class SDVideoTestimonialMobileLoop {
  constructor(root) {
    this.root = root;
    this.scroller = root.querySelector('.sd-video-testimonial__grid');
    this.active = false;
    this.jumping = false;
    this.clones = [];
    this.originals = [];
    this.settleTimer = null;

    this.mql = window.matchMedia('(max-width: 749px)');
    this.onMedia = () => this.sync();
    this.onScroll = () => {
      if (this.jumping) return;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => this.settle(), 120);
    };

    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onMedia);
    } else if (this.mql.addListener) {
      this.mql.addListener(this.onMedia);
    }
  }

  sync() {
    if (this.mql.matches) {
      this.enable();
    } else {
      this.disable();
    }
  }

  getOriginalCards() {
    if (!this.scroller) return [];

    return Array.from(this.scroller.children).filter(
      (child) =>
        child.classList.contains('sd-video-testimonial__card') &&
        !child.classList.contains('sd-video-testimonial__card--clone')
    );
  }

  makeClones() {
    return this.originals.map((card) => {
      const clone = card.cloneNode(true);
      clone.classList.add('sd-video-testimonial__card--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-sd-vt-open');
      clone.removeAttribute('aria-label');
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    });
  }

  enable() {
    if (this.active || !this.scroller) return;

    this.originals = this.getOriginalCards();
    if (this.originals.length < 2) return;

    this.active = true;

    const leadFragment = document.createDocumentFragment();
    const trailFragment = document.createDocumentFragment();
    const lead = this.makeClones();
    const trail = this.makeClones();

    lead.forEach((clone) => leadFragment.appendChild(clone));
    trail.forEach((clone) => trailFragment.appendChild(clone));

    this.scroller.insertBefore(leadFragment, this.originals[0]);
    this.scroller.appendChild(trailFragment);
    this.clones = lead.concat(trail);

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    window.requestAnimationFrame(() => {
      this.centerOriginal(Math.floor(this.originals.length / 2));
    });
  }

  disable() {
    if (!this.active || !this.scroller) return;

    this.active = false;
    this.scroller.removeEventListener('scroll', this.onScroll);
    window.clearTimeout(this.settleTimer);

    this.clones.forEach((clone) => clone.remove());
    this.clones = [];
    this.originals = [];
    this.scroller.scrollLeft = 0;
  }

  centerCard(card) {
    if (!card || !this.scroller) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const delta =
      cardRect.left + cardRect.width / 2 - (scrollerRect.left + this.scroller.clientWidth / 2);

    const previous = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft += delta;
    this.scroller.style.scrollBehavior = previous;
  }

  centerOriginal(index) {
    if (!this.scroller || !this.originals.length) return;

    const child = this.scroller.children[this.originals.length + index];
    this.centerCard(child);
  }

  settle() {
    if (!this.active || !this.scroller) return;

    const children = Array.from(this.scroller.children);
    const len = this.originals.length;

    if (children.length < len * 3) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const center = scrollerRect.left + this.scroller.clientWidth / 2;

    let bestIndex = 0;
    let bestDistance = Infinity;

    children.forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    const setWidth =
      children[len].getBoundingClientRect().left - children[0].getBoundingClientRect().left;

    if (bestIndex < len) {
      this.jumpBy(setWidth);
    } else if (bestIndex >= len * 2) {
      this.jumpBy(-setWidth);
    }
  }

  jumpBy(delta) {
    if (!delta || !this.scroller) return;

    this.jumping = true;
    const previous = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft += delta;
    this.scroller.style.scrollBehavior = previous;

    window.requestAnimationFrame(() => {
      this.jumping = false;
    });
  }

  recenterNearest() {
    if (!this.scroller) return;

    const children = Array.from(this.scroller.children);
    if (!children.length) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const center = scrollerRect.left + this.scroller.clientWidth / 2;

    let best = null;
    let bestDistance = Infinity;

    children.forEach((child) => {
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = child;
      }
    });

    this.centerCard(best);
  }

  onResize() {
    if (!this.active) return;
    this.recenterNearest();
  }

  destroy() {
    this.disable();

    if (this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this.onMedia);
    } else if (this.mql.removeListener) {
      this.mql.removeListener(this.onMedia);
    }
  }
}

class SDVideoTestimonial extends HTMLElement {
  constructor() {
    super();
    this.onGridClick = this.onGridClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.isClosing = false;
    this.closeTimer = null;
    this.activeTrigger = null;
    this.mobileLoop = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-sd-vt-overlay]');
    this.mediaContainer = this.querySelector('[data-sd-vt-media]');
    this.grid = this.querySelector('.sd-video-testimonial__grid');
    this.closeButtons = this.querySelectorAll('[data-sd-vt-close]');

    if (this.overlay && this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }

    this.grid?.addEventListener('click', this.onGridClick);

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.onClose);
    });

    this.overlay?.addEventListener('click', this.onOverlayClick);

    this.mobileLoop = new SDVideoTestimonialMobileLoop(this);
    this.mobileLoop.sync();

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.grid?.removeEventListener('click', this.onGridClick);

    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.onClose);
    });

    this.overlay?.removeEventListener('click', this.onOverlayClick);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeydown);
    window.clearTimeout(this.closeTimer);
    document.body.classList.remove('sd-video-testimonial--open');
    this.mobileLoop?.destroy();
    this.overlay?.remove();
  }

  onResize() {
    this.mobileLoop?.sync();
    this.mobileLoop?.onResize();
  }

  onGridClick(event) {
    const trigger = event.target.closest('[data-sd-vt-open]');
    if (!trigger || trigger.classList.contains('sd-video-testimonial__card--clone')) return;
    this.openVideo(trigger);
  }

  openVideo(trigger) {
    const template = trigger.querySelector('[data-sd-vt-template]');

    if (!this.overlay || !this.mediaContainer || !template || this.isClosing) return;

    window.clearTimeout(this.closeTimer);
    this.activeTrigger = trigger;
    this.mediaContainer.innerHTML = template.innerHTML;

    const video = this.mediaContainer.querySelector('video');
    if (video) {
      video.controls = true;
      video.playsInline = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    this.overlay.hidden = false;
    document.body.classList.add('sd-video-testimonial--open');
    document.addEventListener('keydown', this.onKeydown);

    requestAnimationFrame(() => {
      this.overlay.classList.add('is-open');
    });

    this.closeButtons[0]?.focus();
  }

  onClose() {
    if (!this.overlay || this.overlay.hidden || this.isClosing) return;

    this.isClosing = true;
    this.overlay.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKeydown);

    const video = this.mediaContainer?.querySelector('video');
    if (video) {
      video.pause();
    }

    this.closeTimer = window.setTimeout(() => {
      if (this.mediaContainer) {
        this.mediaContainer.innerHTML = '';
      }

      this.overlay.hidden = true;
      this.isClosing = false;
      document.body.classList.remove('sd-video-testimonial--open');
      this.activeTrigger?.focus();
      this.activeTrigger = null;
    }, 350);
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.onClose();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.onClose();
    }
  }
}

if (!customElements.get('sd-video-testimonial')) {
  customElements.define('sd-video-testimonial', SDVideoTestimonial);
}
